import { 
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp,
  query,
  where,
  orderBy,
  getDocs
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../firebase';

const NEWS_COLLECTION = 'news';

export const NEWS_CATEGORIES = {
  NOTICE: 'notice',
  NOTIFICATION: 'notification',
  CIRCULAR: 'circular'
};
const MAX_IMAGE_SIZE = 1 * 1024 * 1024; // 1MB
const MAX_PDF_SIZE = 3 * 1024 * 1024;   // 3MB

export const validateFile = (file) => {
  if (file.type.startsWith('image/')) {
    if (file.size > MAX_IMAGE_SIZE) {
      throw new Error('Image size should not exceed 1MB');
    }
  } else if (file.type === 'application/pdf') {
    if (file.size > MAX_PDF_SIZE) {
      throw new Error('PDF size should not exceed 3MB');
    }
  } else {
    throw new Error('Only images and PDFs are allowed');
  }
  return true;
};

export const uploadFile = async (file, newsId) => {
  validateFile(file);
  const fileRef = ref(storage, `news/${newsId}/${file.name}`);
  await uploadBytes(fileRef, file);
  const downloadURL = await getDownloadURL(fileRef);
  return {
    url: downloadURL,
    path: `news/${newsId}/${file.name}`,
    type: file.type,
    name: file.name
  };
};

export const getNewsByCategory = async (category) => {
  try {
    const q = query(
      collection(db, NEWS_COLLECTION),
      where('category', '==', category),
      orderBy('timestamp', 'desc')
    );
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      timestamp: doc.data().timestamp?.toMillis ? doc.data().timestamp.toMillis() : null,
      lastUpdated: doc.data().lastUpdated?.toMillis ? doc.data().lastUpdated.toMillis() : null
    }));
  } catch (error) {
    console.error('Error getting news by category:', error);
    throw error;
  }
};

export const getAllNews = async () => {
  try {
    const q = query(
      collection(db, NEWS_COLLECTION),
      orderBy('timestamp', 'desc')
    );
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      timestamp: doc.data().timestamp?.toMillis ? doc.data().timestamp.toMillis() : null,
      lastUpdated: doc.data().lastUpdated?.toMillis ? doc.data().lastUpdated.toMillis() : null
    }));
  } catch (error) {
    console.error('Error getting all news:', error);
    throw error;
  }
};

export const addNews = async (newsData, file = null) => {
  try {
    const docRef = await addDoc(collection(db, NEWS_COLLECTION), {
      ...newsData,
      timestamp: serverTimestamp(),
      author: 'Andheri Courts Bar Association'
    });

    if (file) {
      const fileData = await uploadFile(file, docRef.id);
      await updateDoc(docRef, { attachment: fileData });
    }

    return docRef.id;
  } catch (error) {
    console.error('Error adding news:', error);
    throw error;
  }
};

export const updateNews = async (newsId, newsData, file = null) => {
  try {
    const newsRef = doc(db, NEWS_COLLECTION, newsId);
    
    if (file) {
      // Delete old file if exists
      if (newsData.attachment?.path) {
        const oldFileRef = ref(storage, newsData.attachment.path);
        try {
          await deleteObject(oldFileRef);
        } catch (error) {
          console.error('Error deleting old file:', error);
        }
      }
      
      const fileData = await uploadFile(file, newsId);
      newsData.attachment = fileData;
    }

    await updateDoc(newsRef, {
      ...newsData,
      lastUpdated: serverTimestamp()
    });
  } catch (error) {
    console.error('Error updating news:', error);
    throw error;
  }
};

export const deleteNews = async (newsId, filePath = null) => {
  try {
    if (filePath) {
      const fileRef = ref(storage, filePath);
      try {
        await deleteObject(fileRef);
      } catch (error) {
        console.error('Error deleting file:', error);
      }
    }
    
    const newsRef = doc(db, NEWS_COLLECTION, newsId);
    await deleteDoc(newsRef);
  } catch (error) {
    console.error('Error deleting news:', error);
    throw error;
  }
};
